import { useState, useCallback, type ReactNode } from 'react'
import { Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Code2, Eye, ToggleLeft, ToggleRight } from 'lucide-react'
import Editor from '@monaco-editor/react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/misc'
import type { ExcelConfig, ExcelSheetConfig, ExcelColumnConfig, ExcelCellStyle, DataSource } from '@/types'
import { DEFAULT_EXCEL_STYLE_DEFAULTS } from '@/types'

interface Props {
  config: ExcelConfig
  onChange: (config: ExcelConfig) => void
  activeSources: DataSource[]          // sources toggled on in this template
  previewColumns: Record<string, string[]>  // key → column names from schema/preview
}

const DEFAULT_SHEET = (): ExcelSheetConfig => ({
  dataSourceKey: '',
  sheetName: '',
  freezeHeader: true,
  autoFilter: true,
  groupByField: undefined,
  showSubtotals: false,
  columns: [],
})

const FORMAT_PRESETS = [
  { label: 'None',       value: '' },
  { label: 'Date',       value: 'dd/mm/yyyy' },
  { label: 'DateTime',   value: 'dd/mm/yyyy hh:mm' },
  { label: 'Integer',    value: '#,##0' },
  { label: 'Decimal 2',  value: '#,##0.00' },
  { label: 'Currency €', value: '€#,##0.00' },
  { label: 'Percent',    value: '0.00%' },
]

function fieldOptions(sheet: ExcelSheetConfig, availCols: string[]): string[] {
  const saved = sheet.columns.map(c => c.field).filter(Boolean)
  return [...new Set([...availCols, ...saved])]
}

const DEFAULT_STYLE_PATCH = (): Pick<ExcelConfig, 'defaultFontName' | 'defaultFontSize' | 'headerStyle' | 'bodyStyle' | 'alternatingRows' | 'alternatingBackgroundColor'> => ({
  defaultFontName: DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontName,
  defaultFontSize: DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontSize,
  headerStyle: {
    fontName: '',
    fontSize: undefined,
    fontColor: DEFAULT_EXCEL_STYLE_DEFAULTS.headerFontColor,
    backgroundColor: DEFAULT_EXCEL_STYLE_DEFAULTS.headerBackgroundColor,
  },
  bodyStyle: {
    fontName: '',
    fontSize: undefined,
    fontColor: DEFAULT_EXCEL_STYLE_DEFAULTS.bodyFontColor,
    backgroundColor: '',
  },
  alternatingRows: DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingRows,
  alternatingBackgroundColor: DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingBackgroundColor,
})

function normalizeHexColor(value: string, fallback: string): string {
  const v = value.trim()
  if (/^#[0-9a-fA-F]{3}$/.test(v) || /^#[0-9a-fA-F]{6}$/.test(v)) return v
  return fallback
}

function toColorInputValue(hex: string | undefined, fallback: string): string {
  const normalized = normalizeHexColor(hex ?? '', fallback)
  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) return normalized
  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    const r = normalized[1] + normalized[1]
    const g = normalized[2] + normalized[2]
    const b = normalized[3] + normalized[3]
    return `#${r}${g}${b}`
  }
  return fallback
}

export function ExcelLayoutEditor({ config, onChange, activeSources, previewColumns }: Props) {
  const [showJson, setShowJson] = useState(false)
  const [styleExpanded, setStyleExpanded] = useState(true)
  const [expandedSheets, setExpandedSheets] = useState<Set<number>>(new Set([0]))
  const [dragOverCol, setDragOverCol] = useState<{ sheet: number; col: number } | null>(null)
  const [dragCol, setDragCol] = useState<{ sheet: number; col: number } | null>(null)

  const update = useCallback((patch: Partial<ExcelConfig>) => {
    onChange({ ...config, ...patch })
  }, [config, onChange])

  function updateSheet(i: number, patch: Partial<ExcelSheetConfig>) {
    const sheets = config.sheets.map((s, idx) => idx === i ? { ...s, ...patch } : s)
    update({ sheets })
  }

  function addSheet() {
    const first = activeSources.find(ds => !config.sheets.some(s => s.dataSourceKey === ds.key))
    const sheet = { ...DEFAULT_SHEET(), dataSourceKey: first?.key ?? '', sheetName: first?.key ?? '' }
    const sheets = [...config.sheets, sheet]
    update({ sheets })
    setExpandedSheets(s => new Set([...s, sheets.length - 1]))
  }

  function removeSheet(i: number) {
    update({ sheets: config.sheets.filter((_, idx) => idx !== i) })
  }

  function addColumn(si: number) {
    const sheet = config.sheets[si]
    const col: ExcelColumnConfig = { field: '', header: '', width: undefined, format: '', hidden: false }
    updateSheet(si, { columns: [...sheet.columns, col] })
  }

  function updateColumn(si: number, ci: number, patch: Partial<ExcelColumnConfig>) {
    const cols = config.sheets[si].columns.map((c, idx) => idx === ci ? { ...c, ...patch } : c)
    updateSheet(si, { columns: cols })
  }

  function removeColumn(si: number, ci: number) {
    updateSheet(si, { columns: config.sheets[si].columns.filter((_, idx) => idx !== ci) })
  }

  function addAllColumns(si: number) {
    const sheet = config.sheets[si]
    const available = previewColumns[sheet.dataSourceKey] ?? []
    const existing  = new Set(sheet.columns.map(c => c.field))
    const newCols   = available
      .filter(f => !existing.has(f))
      .map(f => ({ field: f, header: f, width: undefined, format: '', hidden: false }))
    updateSheet(si, { columns: [...sheet.columns, ...newCols] })
  }

  // Drag-to-reorder columns
  function handleColDragStart(si: number, ci: number) {
    setDragCol({ sheet: si, col: ci })
  }

  function handleColDragOver(e: React.DragEvent, si: number, ci: number) {
    e.preventDefault()
    setDragOverCol({ sheet: si, col: ci })
  }

  function handleColDrop(si: number, targetCi: number) {
    if (!dragCol || dragCol.sheet !== si) return
    const cols = [...config.sheets[si].columns]
    const [moved] = cols.splice(dragCol.col, 1)
    cols.splice(targetCi, 0, moved)
    updateSheet(si, { columns: cols })
    setDragCol(null)
    setDragOverCol(null)
  }

  function toggleSheet(i: number) {
    setExpandedSheets(s => {
      const next = new Set(s)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  function updateHeaderStyle(patch: Partial<ExcelCellStyle>) {
    update({ headerStyle: { ...config.headerStyle, ...patch } })
  }

  function updateBodyStyle(patch: Partial<ExcelCellStyle>) {
    update({ bodyStyle: { ...config.bodyStyle, ...patch } })
  }

  function resetWorkbookStyle() {
    update(DEFAULT_STYLE_PATCH())
  }

  const headerStyle = config.headerStyle ?? DEFAULT_STYLE_PATCH().headerStyle!
  const bodyStyle = config.bodyStyle ?? DEFAULT_STYLE_PATCH().bodyStyle!
  const defaultFontName = config.defaultFontName ?? DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontName
  const defaultFontSize = config.defaultFontSize ?? DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontSize
  const alternatingRows = config.alternatingRows ?? DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingRows
  const alternatingBackgroundColor = config.alternatingBackgroundColor ?? DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingBackgroundColor
  const headerBg = normalizeHexColor(headerStyle.backgroundColor ?? '', DEFAULT_EXCEL_STYLE_DEFAULTS.headerBackgroundColor)
  const headerColor = normalizeHexColor(headerStyle.fontColor ?? '', DEFAULT_EXCEL_STYLE_DEFAULTS.headerFontColor)
  const bodyColor = normalizeHexColor(bodyStyle.fontColor ?? '', DEFAULT_EXCEL_STYLE_DEFAULTS.bodyFontColor)
  const bodyBg = bodyStyle.backgroundColor ? normalizeHexColor(bodyStyle.backgroundColor, '') : ''
  const altBg = normalizeHexColor(alternatingBackgroundColor, DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingBackgroundColor)

  // JSON toggle — parse incoming edits back to config
  function handleJsonChange(raw: string | undefined) {
    try {
      const parsed = JSON.parse(raw ?? '{}') as ExcelConfig
      onChange(parsed)
    } catch { /* ignore parse errors while typing */ }
  }

  const jsonValue = JSON.stringify(config, null, 2)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2 flex-shrink-0">
        <span className="text-[10px] font-bold tracking-widest uppercase text-muted flex-1">Excel Layout</span>

        {/* Summary sheet toggle */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <button
            onClick={() => update({ includeSummarySheet: !config.includeSummarySheet })}
            className="text-muted hover:text-accent transition-colors"
          >
            {config.includeSummarySheet
              ? <ToggleRight className="h-4 w-4 text-accent" />
              : <ToggleLeft className="h-4 w-4" />}
          </button>
          <span className="text-[10px] font-mono text-muted">Summary sheet</span>
        </label>

        {config.includeSummarySheet && (
          <input
            className="w-28 rounded border border-border2 bg-bg px-2 py-1 text-[10px] font-mono text-ink focus:border-accent focus:outline-none"
            placeholder="Summary"
            value={config.summarySheetName}
            onChange={e => update({ summarySheetName: e.target.value })}
          />
        )}

        {/* JSON toggle */}
        <button
          onClick={() => setShowJson(p => !p)}
          className={cn(
            'flex items-center gap-1.5 rounded px-2 py-1 text-[10px] font-mono transition-colors',
            showJson ? 'bg-accent/10 text-accent' : 'text-muted hover:text-ink'
          )}
        >
          {showJson ? <Eye className="h-3 w-3" /> : <Code2 className="h-3 w-3" />}
          {showJson ? 'Visual' : 'JSON'}
        </button>

        <Button size="sm" variant="ghost" onClick={addSheet}>
          <Plus className="h-3 w-3" /> Add Sheet
        </Button>
      </div>

      {showJson ? (
        /* ── JSON editor ── */
        <div className="flex-1 overflow-hidden">
          <Editor
            height="100%"
            language="json"
            theme="vs-dark"
            value={jsonValue}
            onChange={handleJsonChange}
            options={{
              fontSize: 12,
              fontFamily: "'JetBrains Mono', monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              tabSize: 2,
              padding: { top: 12 },
              scrollbar: { verticalScrollbarSize: 6 },
            }}
          />
        </div>
      ) : (
        /* ── Visual editor ── */
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Workbook style */}
          <div className="rounded-lg border border-border bg-surface overflow-hidden">
            <div
              className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-border/20 transition-colors select-none"
              onClick={() => setStyleExpanded(e => !e)}
            >
              {styleExpanded
                ? <ChevronDown className="h-3.5 w-3.5 text-muted flex-shrink-0" />
                : <ChevronRight className="h-3.5 w-3.5 text-muted flex-shrink-0" />}
              <span className="text-xs font-bold text-ink flex-1">Workbook Style</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={e => { e.stopPropagation(); resetWorkbookStyle() }}
              >
                Reset defaults
              </Button>
            </div>

            {styleExpanded && (
              <div className="border-t border-border px-4 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StyleField label="Default font name">
                    <input
                      className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                      placeholder={DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontName}
                      value={defaultFontName}
                      onChange={e => update({ defaultFontName: e.target.value })}
                    />
                  </StyleField>
                  <StyleField label="Default font size">
                    <input
                      type="number"
                      min={6}
                      max={72}
                      className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                      value={defaultFontSize}
                      onChange={e => update({ defaultFontSize: Number(e.target.value) || DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontSize })}
                    />
                  </StyleField>
                </div>

                <div className="space-y-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted">Header row</span>
                  <div className="grid grid-cols-2 gap-3">
                    <StyleField label="Font name">
                      <input
                        className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                        placeholder={`Inherit (${defaultFontName})`}
                        value={headerStyle.fontName ?? ''}
                        onChange={e => updateHeaderStyle({ fontName: e.target.value })}
                      />
                    </StyleField>
                    <StyleField label="Font size">
                      <input
                        type="number"
                        min={6}
                        max={72}
                        className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                        placeholder={`Inherit (${defaultFontSize})`}
                        value={headerStyle.fontSize ?? ''}
                        onChange={e => updateHeaderStyle({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </StyleField>
                    <ColorField
                      label="Font color"
                      value={headerStyle.fontColor ?? DEFAULT_EXCEL_STYLE_DEFAULTS.headerFontColor}
                      fallback={DEFAULT_EXCEL_STYLE_DEFAULTS.headerFontColor}
                      onChange={v => updateHeaderStyle({ fontColor: v })}
                    />
                    <ColorField
                      label="Background color"
                      value={headerStyle.backgroundColor ?? DEFAULT_EXCEL_STYLE_DEFAULTS.headerBackgroundColor}
                      fallback={DEFAULT_EXCEL_STYLE_DEFAULTS.headerBackgroundColor}
                      onChange={v => updateHeaderStyle({ backgroundColor: v })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted">Body rows</span>
                  <div className="grid grid-cols-2 gap-3">
                    <StyleField label="Font name">
                      <input
                        className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                        placeholder={`Inherit (${defaultFontName})`}
                        value={bodyStyle.fontName ?? ''}
                        onChange={e => updateBodyStyle({ fontName: e.target.value })}
                      />
                    </StyleField>
                    <StyleField label="Font size">
                      <input
                        type="number"
                        min={6}
                        max={72}
                        className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                        placeholder={`Inherit (${defaultFontSize})`}
                        value={bodyStyle.fontSize ?? ''}
                        onChange={e => updateBodyStyle({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </StyleField>
                    <ColorField
                      label="Font color"
                      value={bodyStyle.fontColor ?? DEFAULT_EXCEL_STYLE_DEFAULTS.bodyFontColor}
                      fallback={DEFAULT_EXCEL_STYLE_DEFAULTS.bodyFontColor}
                      onChange={v => updateBodyStyle({ fontColor: v })}
                    />
                    <ColorField
                      label="Background color"
                      value={bodyStyle.backgroundColor ?? ''}
                      fallback="#ffffff"
                      onChange={v => updateBodyStyle({ backgroundColor: v })}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-4 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <button
                        type="button"
                        onClick={() => update({ alternatingRows: !alternatingRows })}
                        className="text-muted hover:text-accent transition-colors"
                      >
                        {alternatingRows
                          ? <ToggleRight className="h-4 w-4 text-accent" />
                          : <ToggleLeft className="h-4 w-4" />}
                      </button>
                      <span className="text-[10px] font-mono text-muted">Alternating rows</span>
                    </label>
                    {alternatingRows && (
                      <div className="flex-1 min-w-[200px]">
                        <ColorField
                          label="Alternating background"
                          value={alternatingBackgroundColor}
                          fallback={DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingBackgroundColor}
                          onChange={v => update({ alternatingBackgroundColor: v })}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded border border-border2 overflow-hidden max-w-xs">
                  <div
                    className="px-3 py-2 text-xs font-bold"
                    style={{
                      fontFamily: headerStyle.fontName || defaultFontName,
                      fontSize: headerStyle.fontSize ?? defaultFontSize,
                      color: headerColor,
                      backgroundColor: headerBg,
                    }}
                  >
                    Header preview
                  </div>
                  <div
                    className="px-3 py-2 text-xs"
                    style={{
                      fontFamily: bodyStyle.fontName || defaultFontName,
                      fontSize: bodyStyle.fontSize ?? defaultFontSize,
                      color: bodyColor,
                      backgroundColor: bodyBg || '#ffffff',
                    }}
                  >
                    Body row 1
                  </div>
                  <div
                    className="px-3 py-2 text-xs"
                    style={{
                      fontFamily: bodyStyle.fontName || defaultFontName,
                      fontSize: bodyStyle.fontSize ?? defaultFontSize,
                      color: bodyColor,
                      backgroundColor: alternatingRows ? altBg : (bodyBg || '#ffffff'),
                    }}
                  >
                    Body row 2
                  </div>
                </div>
              </div>
            )}
          </div>

          {config.sheets.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-xs text-muted font-mono mb-3">No sheets configured yet.</p>
              <Button size="sm" variant="ghost" onClick={addSheet}>
                <Plus className="h-3 w-3" /> Add your first sheet
              </Button>
            </div>
          )}

          {config.sheets.map((sheet, si) => {
            const expanded = expandedSheets.has(si)
            const availCols = previewColumns[sheet.dataSourceKey] ?? []
            const colOptions = fieldOptions(sheet, availCols)

            return (
              <div key={si} className="rounded-lg border border-border bg-surface overflow-hidden">
                {/* Sheet header */}
                <div
                  className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-border/20 transition-colors select-none"
                  onClick={() => toggleSheet(si)}
                >
                  {expanded
                    ? <ChevronDown className="h-3.5 w-3.5 text-muted flex-shrink-0" />
                    : <ChevronRight className="h-3.5 w-3.5 text-muted flex-shrink-0" />}

                  <span className="text-xs font-bold text-ink flex-1 truncate">
                    {sheet.sheetName || sheet.dataSourceKey || `Sheet ${si + 1}`}
                  </span>

                  <Badge variant={sheet.dataSourceKey ? 'green' : 'default'} className="flex-shrink-0">
                    {sheet.dataSourceKey || 'no source'}
                  </Badge>
                  <span className="text-[10px] text-muted font-mono flex-shrink-0">
                    {sheet.columns.length} col{sheet.columns.length !== 1 ? 's' : ''}
                  </span>

                  <button
                    onClick={e => { e.stopPropagation(); removeSheet(si) }}
                    className="text-muted hover:text-red transition-colors flex-shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {expanded && (
                  <div className="border-t border-border px-4 py-4 space-y-4">
                    {/* Sheet settings row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold tracking-widest uppercase text-muted">Data Source</label>
                        <select
                          className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-green focus:border-accent focus:outline-none"
                          value={sheet.dataSourceKey}
                          onChange={e => updateSheet(si, { dataSourceKey: e.target.value, sheetName: sheet.sheetName || e.target.value })}
                        >
                          <option value="">— select —</option>
                          {activeSources.map(ds => (
                            <option key={ds.key} value={ds.key}>{ds.key}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold tracking-widest uppercase text-muted">Sheet Name</label>
                        <input
                          className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                          placeholder={sheet.dataSourceKey || 'Sheet name…'}
                          maxLength={31}
                          value={sheet.sheetName}
                          onChange={e => updateSheet(si, { sheetName: e.target.value })}
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold tracking-widest uppercase text-muted">Group By Field</label>
                        <select
                          className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                          value={sheet.groupByField ?? ''}
                          onChange={e => updateSheet(si, { groupByField: e.target.value || undefined })}
                        >
                          <option value="">— none —</option>
                          {colOptions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>

                      <div className="flex flex-col justify-end gap-2">
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <button
                              type="button"
                              onClick={() => updateSheet(si, { freezeHeader: !sheet.freezeHeader })}
                              className="text-muted hover:text-accent transition-colors"
                            >
                              {sheet.freezeHeader
                                ? <ToggleRight className="h-4 w-4 text-accent" />
                                : <ToggleLeft className="h-4 w-4" />}
                            </button>
                            <span className="text-[10px] font-mono text-muted">Freeze header</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <button
                              type="button"
                              onClick={() => updateSheet(si, { autoFilter: !sheet.autoFilter })}
                              className="text-muted hover:text-accent transition-colors"
                            >
                              {sheet.autoFilter
                                ? <ToggleRight className="h-4 w-4 text-accent" />
                                : <ToggleLeft className="h-4 w-4" />}
                            </button>
                            <span className="text-[10px] font-mono text-muted">Auto-filter</span>
                          </label>
                          {sheet.groupByField && (
                            <Toggle
                              label="Subtotals"
                              value={sheet.showSubtotals}
                              onChange={v => updateSheet(si, { showSubtotals: v })}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Columns */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold tracking-widest uppercase text-muted">Columns</span>
                        <div className="flex items-center gap-2">
                          {colOptions.length > 0 && (
                            <button
                              onClick={() => addAllColumns(si)}
                              className="text-[10px] font-mono text-accent hover:underline"
                            >
                              + Add all from source
                            </button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => addColumn(si)}>
                            <Plus className="h-3 w-3" /> Add
                          </Button>
                        </div>
                      </div>

                      {sheet.columns.length === 0 ? (
                        <p className="text-[10px] text-muted font-mono px-1 py-2">
                          No columns — all fields will be exported automatically.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {/* Column list header */}
                          <div className="grid gap-2 px-2 pb-1" style={{ gridTemplateColumns: '20px 1fr 1fr 80px 130px 36px 36px' }}>
                            {['', 'Field', 'Header', 'Width', 'Format', 'Hide', ''].map((h, i) => (
                              <span key={i} className="text-[9px] font-bold tracking-widest uppercase text-muted">{h}</span>
                            ))}
                          </div>

                          {sheet.columns.map((col, ci) => (
                            <div
                              key={ci}
                              draggable
                              onDragStart={() => handleColDragStart(si, ci)}
                              onDragOver={e => handleColDragOver(e, si, ci)}
                              onDrop={() => handleColDrop(si, ci)}
                              onDragEnd={() => { setDragCol(null); setDragOverCol(null) }}
                              className={cn(
                                'grid gap-2 items-center rounded px-2 py-1.5 transition-colors',
                                dragOverCol?.sheet === si && dragOverCol?.col === ci
                                  ? 'bg-accent/10 ring-1 ring-accent/30'
                                  : 'hover:bg-border/30',
                                col.hidden && 'opacity-40'
                              )}
                              style={{ gridTemplateColumns: '20px 1fr 1fr 80px 130px 36px 36px' }}
                            >
                              {/* Drag handle */}
                              <GripVertical className="h-3.5 w-3.5 text-muted cursor-grab active:cursor-grabbing" />

                              {/* Field */}
                              <select
                                className="rounded border border-border2 bg-bg px-1.5 py-1 text-[11px] font-mono text-green focus:border-accent focus:outline-none w-full"
                                value={col.field}
                                onChange={e => updateColumn(si, ci, { field: e.target.value, header: col.header || e.target.value })}
                              >
                                <option value="">— field —</option>
                                {colOptions.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>

                              {/* Header label */}
                              <input
                                className="rounded border border-border2 bg-bg px-1.5 py-1 text-[11px] font-mono text-ink focus:border-accent focus:outline-none w-full"
                                placeholder={col.field || 'Label…'}
                                value={col.header}
                                onChange={e => updateColumn(si, ci, { header: e.target.value })}
                              />

                              {/* Width */}
                              <input
                                type="number"
                                className="rounded border border-border2 bg-bg px-1.5 py-1 text-[11px] font-mono text-ink focus:border-accent focus:outline-none w-full"
                                placeholder="auto"
                                min={5} max={100}
                                value={col.width ?? ''}
                                onChange={e => updateColumn(si, ci, { width: e.target.value ? Number(e.target.value) : undefined })}
                              />

                              {/* Format preset */}
                              <select
                                className="rounded border border-border2 bg-bg px-1.5 py-1 text-[11px] font-mono text-ink focus:border-accent focus:outline-none w-full"
                                value={col.format ?? ''}
                                onChange={e => updateColumn(si, ci, { format: e.target.value })}
                              >
                                {FORMAT_PRESETS.map(p => (
                                  <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                                {col.format && !FORMAT_PRESETS.some(p => p.value === col.format) && (
                                  <option value={col.format}>{col.format}</option>
                                )}
                              </select>

                              {/* Hide toggle */}
                              <button
                                onClick={() => updateColumn(si, ci, { hidden: !col.hidden })}
                                className={cn(
                                  'flex items-center justify-center rounded transition-colors h-6 w-8',
                                  col.hidden ? 'text-muted' : 'text-accent'
                                )}
                                title={col.hidden ? 'Hidden' : 'Visible'}
                              >
                                {col.hidden
                                  ? <ToggleLeft className="h-4 w-4" />
                                  : <ToggleRight className="h-4 w-4" />}
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => removeColumn(si, ci)}
                                className="flex items-center justify-center text-muted hover:text-red transition-colors h-6 w-8"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Style panel helpers ─────────────────────────────────────────────────────

function StyleField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold tracking-widest uppercase text-muted">{label}</label>
      {children}
    </div>
  )
}

function ColorField({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string
  value: string
  fallback: string
  onChange: (value: string) => void
}) {
  const display = value || fallback
  const pickerValue = toColorInputValue(display, fallback)

  return (
    <StyleField label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          className="h-8 w-10 cursor-pointer rounded border border-border2 bg-bg p-0.5"
          value={pickerValue}
          onChange={e => onChange(e.target.value)}
        />
        <input
          className="flex-1 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
          placeholder={fallback}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={e => onChange(normalizeHexColor(e.target.value, fallback))}
        />
      </div>
    </StyleField>
  )
}

// ─── Small toggle helper ──────────────────────────────────────────────────────

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none">
      <button
        onClick={() => onChange(!value)}
        className={cn('relative h-4 w-8 rounded-full transition-colors flex-shrink-0', value ? 'bg-accent' : 'bg-border2')}
      >
        <span className={cn('absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform', value ? 'translate-x-4' : 'translate-x-0.5')} />
      </button>
      <span className="text-[10px] font-mono text-muted">{label}</span>
    </label>
  )
}
